import type { PlatoCommands, PlatoGlobal } from '@plato/embed';

/** Where Plato serves the loader. A placeholder until the domains are registered. */
export const DEFAULT_EMBED_ORIGIN = 'https://embed.plato.example';

export interface LoadPlatoOptions {
  /** Where the loader is served from. Only for local development or a self-hosted copy. */
  readonly embedOrigin?: string;
}

/** The `plato(command, ...args)` function, safe to keep: it always calls the current global. */
export type Plato = <C extends keyof PlatoCommands>(command: C, ...args: PlatoCommands[C]) => void;

type PlatoWindow = Window & typeof globalThis & { plato?: PlatoGlobal };

let loading: Promise<Plato> | null = null;

/**
 * Adds Plato's loader script to the page once and resolves with `plato` when it has run. Calls
 * made before that are queued and replayed by the loader, so `plato` may be used at once.
 * The first call's `embedOrigin` wins. Rejects when the script cannot be loaded.
 */
export function loadPlato(options: LoadPlatoOptions = {}): Promise<Plato> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('loadPlato runs in a browser'));
  }
  loading ??= addLoader(window as PlatoWindow, options.embedOrigin ?? DEFAULT_EMBED_ORIGIN).catch(
    (error: unknown) => {
      loading = null;
      throw error;
    },
  );
  return loading;
}

/**
 * Calls the page's `plato` global, defining the queue first if the loader has not run. Use it
 * for `open`, `send`, `setContext`, `logout` and the rest.
 */
export const plato: Plato = (command, ...args) => {
  const win = window as PlatoWindow;
  installQueue(win)(command, ...args);
};

async function addLoader(win: PlatoWindow, embedOrigin: string): Promise<Plato> {
  const queue = installQueue(win);
  if (queue.version !== undefined) return plato;
  const src = loaderUrl(embedOrigin);
  const doc = win.document;
  const script =
    [...doc.scripts].find((candidate) => candidate.src === src) ?? appendScript(doc, src);
  return new Promise((resolve, reject) => {
    script.addEventListener('load', () => resolve(plato), { once: true });
    script.addEventListener(
      'error',
      () => reject(new Error(`Plato's loader could not be loaded from ${src}`)),
      { once: true },
    );
  });
}

/** The snippet's queue, as `QUEUE_SNIPPET` in `@plato/embed` defines it. */
function installQueue(win: PlatoWindow): PlatoGlobal {
  if (!win.plato) {
    const queue: PlatoGlobal = (...args: unknown[]) => {
      queue.q = queue.q ?? [];
      queue.q.push(args);
    };
    win.plato = queue;
  }
  return win.plato;
}

function appendScript(doc: Document, src: string): HTMLScriptElement {
  const script = doc.createElement('script');
  script.async = true;
  script.src = src;
  doc.head.append(script);
  return script;
}

function loaderUrl(embedOrigin: string): string {
  const url = new URL(embedOrigin);
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(url.hostname);
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && local)) {
    throw new Error(
      `embedOrigin must be https (http is allowed for localhost only): ${embedOrigin}`,
    );
  }
  return `${url.origin}/plato.js`;
}
